import React, { useEffect, useState } from "react"

import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useSubTeams } from "@/hooks/useSubTeams"
import { UpdateUserData, useUpdateUser } from "@/hooks/useUpdateUser"
import { User } from "@/hooks/useUsers"

import { Constants } from "../../../database.types"

interface EditUserDialogProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  user: User | null
}

export const EditUserDialog: React.FC<EditUserDialogProps> = ({ isOpen, setIsOpen, user }) => {
  const [formData, setFormData] = useState<{
    fullName: string
    role: string
    team: string
    sub_teams: string[]
    dsp_id?: string
  }>({
    fullName: "",
    role: "Admin",
    team: "",
    sub_teams: [],
    dsp_id: "",
  })
  const [selectedSubTeams, setSelectedSubTeams] = useState<string[]>([])

  // Use the hook to fetch sub-teams
  const { subTeamOptions, subTeamOptionsWithIds } = useSubTeams()

  // Filter subteam options based on role
  const filteredSubTeamOptions = React.useMemo(() => {
    if (formData.role === "DSP") {
      return subTeamOptionsWithIds
        .filter((option) => option.type === "DSP")
        .map((t) => ({ value: t.value, label: t.label }))
    }
    // Managers and other roles see all options
    return subTeamOptions
  }, [formData.role, subTeamOptions, subTeamOptionsWithIds])

  // Use the update user mutation
  const updateUser = useUpdateUser()

  // Handle sub-team selection with special logic for DSP role
  const handleSubTeamChange = (newSubTeams: string[]) => {
    // For DSP role, only allow single selection (but store as array)
    if (formData.role === "DSP") {
      // Take only the last selected item (most recent selection)
      const singleSelection = newSubTeams.length > 0 ? [newSubTeams[newSubTeams.length - 1]] : []
      setSelectedSubTeams(singleSelection)

      // Auto-set dsp_id when DSP sub-team is selected
      if (singleSelection.length > 0) {
        const selectedDsp = subTeamOptionsWithIds.find((t) => t.value === singleSelection[0])
        if (selectedDsp) {
          setFormData((prev) => ({
            ...prev,
            dsp_id: selectedDsp.id,
          }))
        }
      } else {
        // Clear dsp_id if no sub-team selected
        setFormData((prev) => ({
          ...prev,
          dsp_id: "",
        }))
      }
    } else {
      // For other roles, allow multiple selections
      setSelectedSubTeams(newSubTeams)
    }
  }

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || "",
        role: user.role || "Admin",
        team: user.team || "",
        sub_teams: user.sub_teams || [],
        dsp_id: user.dsp_id || "",
      })

      // Set sub_teams if available, filtering based on the user's role
      if (user.sub_teams && Array.isArray(user.sub_teams)) {
        const userRole = user.role || "Admin"
        let validSubTeams = user.sub_teams

        // Filter existing subteams based on role (only DSP role is filtered)
        if (userRole === "DSP" && subTeamOptionsWithIds.length > 0) {
          const dspValues = subTeamOptionsWithIds
            .filter((option) => option.type === "DSP")
            .map((t) => t.value)
          validSubTeams = user.sub_teams.filter((st) => dspValues.includes(st))
        }
        // Managers and other roles keep all their subteams

        setSelectedSubTeams(validSubTeams)
      } else {
        setSelectedSubTeams([])
      }
    }
  }, [subTeamOptionsWithIds, user])

  // Note: Sub-teams are now loaded via useSubTeams hook

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!user) return

    // Validate form data
    if (!formData.fullName) {
      toast.error("Please fill in all required fields")
      return
    }

    // Prepare user data for submission
    const userData: UpdateUserData = {
      userId: user.id,
      fullName: formData.fullName,
      team: formData.team,
      role: formData.role,
      sub_teams: selectedSubTeams,
      ...(formData.role === "DSP" && formData.dsp_id ? { dsp_id: formData.dsp_id } : {}),
    }

    // Submit the user data
    updateUser.mutate(userData, {
      onSuccess: () => {
        toast.success("User updated successfully")
        setIsOpen(false)
      },
      onError: (error) => {
        toast.error(
          `Failed to update user: ${error instanceof Error ? error.message : "Unknown error"}`,
        )
      },
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update the user's information. Click save when you're done.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                placeholder="John Doe"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">User Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    role: value,
                    dsp_id: "", // Clear dsp_id when role changes
                  }))
                  // Clear selected subteams when role changes to prevent invalid selections
                  setSelectedSubTeams([])
                }}
              >
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="DSP">DSP</SelectItem>
                  <SelectItem value="Driver">Driver</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="team">Team</Label>
              <Select
                value={formData.team}
                onValueChange={(value) => {
                  setFormData((prev) => ({ ...prev, team: value }))
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {Constants.public.Enums.app_team.map((team) => (
                    <SelectItem key={team} value={team}>
                      {team}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sub_teams">{formData.role === "DSP" ? "DSP" : "Sub Teams"}</Label>
              <MultiSelect
                options={filteredSubTeamOptions}
                selected={selectedSubTeams}
                onChange={handleSubTeamChange}
                placeholder={formData.role === "DSP" ? "Select DSP" : "Select sub teams"}
              />
              {formData.role === "DSP" && selectedSubTeams.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  DSP ID will be auto-set: {formData.dsp_id}
                </p>
              )}
            </div>

            {/* // TODO: Implement */}
            {/* {formData.role === "manager" && (
              <div className="space-y-3 p-3 border rounded-md">
                <Label className="block mb-2">
                  Select Job Postings to Manage
                </Label>
                {mockJobPostings.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center space-x-2 py-1"
                  >
                    <Checkbox
                      id={`job-${job.id}`}
                      onCheckedChange={(checked) =>
                        handleJobPostingChange(job.id, checked === true)
                      }
                    />
                    <Label
                      htmlFor={`job-${job.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {job.title}
                    </Label>
                  </div>
                ))}
              </div>
            )} */}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={updateUser.isPending}>
              {updateUser.isPending ? "Updating..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
